import { Modal, Tabs, Table, Spin } from "antd";
import { StaffAvailability } from "~/utils/constants";
import dayjs from "dayjs";
import { useState, useMemo } from "react";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";

interface StaffInfo {
  staffId: string;
  availability: StaffAvailability;
  note?: string;
  staffName?: string;
  isDriver?: boolean;
  isLead?: boolean;
  bookedHours?: number;
  availableHours?: number;
  /** Original availability status when staff is shown in a different category due to bookings */
  originalAvailability?: StaffAvailability | 'PENDING';
}

interface PendingStaffInfo {
  staffId: string;
  staffName?: string;
  isDriver?: boolean;
  isLead?: boolean;
}

interface StaffDetailsSidebarProps {
  selectedDate: dayjs.Dayjs | null;
  staffInfo: StaffInfo[];
  pendingStaffInfo: PendingStaffInfo[];
  availableCount: number;
  unavailableCount: number;
  pendingCount: number;
  scheduledCount?: number;
  trulyAvailableCount?: number;
}

export const StaffDetailsSidebar: React.FC<StaffDetailsSidebarProps> = ({
  selectedDate,
  staffInfo,
  pendingStaffInfo,
  availableCount,
  unavailableCount,
  pendingCount,
}) => {
  const [selectedStaff, setSelectedStaff] = useState<StaffInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch service tickets for the selected staff on the selected date (for modal)
  const startOfDay = selectedDate?.startOf('day').toISOString();
  const endOfDay = selectedDate?.endOf('day').toISOString();

  const { data: serviceTicketsData, isLoading: loadingTickets } = useGetServiceTickets(
    {
      staffId: selectedStaff?.staffId,
      startDate: startOfDay,
      endDate: endOfDay,
      pageSize: 100,
    },
    !!selectedStaff && isModalOpen
  );

  // Fetch ALL service tickets for this date to check pending staff assignments
  const { data: allServiceTicketsData, isLoading: loadingAllTickets } = useGetServiceTickets(
    {
      startDate: startOfDay,
      endDate: endOfDay,
      pageSize: 500,
    },
    !!selectedDate
  );

  // Calculate booked hours for pending staff from service tickets
  const pendingStaffWithBookings = useMemo(() => {
    if (!allServiceTicketsData?.items || !pendingStaffInfo.length) return { scheduled: [], remaining: pendingStaffInfo };

    const pendingStaffMap = new Map<string, { bookedHours: number; staffInfo: PendingStaffInfo }>();

    // Initialize all pending staff
    pendingStaffInfo.forEach(staff => {
      pendingStaffMap.set(staff.staffId, { bookedHours: 0, staffInfo: staff });
    });

    // Check each service ticket for pending staff assignments
    allServiceTicketsData.items.forEach((ticket: any) => {
      const staffsOnTicket = ticket.staffs || [];
      const ticketDuration = Number(ticket.duration) || 0;
      const travelTime = ticket.location?.travelTime ? Number(ticket.location.travelTime) / 60 : 0;
      const totalHours = ticketDuration + travelTime;

      staffsOnTicket.forEach((staff: any) => {
        if (pendingStaffMap.has(staff.id)) {
          const existing = pendingStaffMap.get(staff.id)!;
          existing.bookedHours += totalHours;
        }
      });
    });

    // Separate scheduled (has bookings) from remaining pending
    const scheduled: StaffInfo[] = [];
    const remaining: PendingStaffInfo[] = [];

    pendingStaffMap.forEach(({ bookedHours, staffInfo }) => {
      if (bookedHours > 0) {
        scheduled.push({
          staffId: staffInfo.staffId,
          staffName: staffInfo.staffName,
          isDriver: staffInfo.isDriver,
          isLead: staffInfo.isLead,
          availability: StaffAvailability.FULL_TIME, // placeholder
          bookedHours,
          availableHours: Math.max(0, 8 - bookedHours),
          originalAvailability: 'PENDING',
        });
      } else {
        remaining.push(staffInfo);
      }
    });

    return { scheduled, remaining };
  }, [allServiceTicketsData, pendingStaffInfo]);

  const handleStaffClick = (staff: StaffInfo) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStaff(null);
  };

  if (!selectedDate) {
    return (
      <div className="border rounded-lg p-6 bg-gray-50 h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-lg mb-2">Select a date</div>
          <div className="text-sm">Click on a calendar date to view staff details</div>
        </div>
      </div>
    );
  }

  // Split staff into categories, considering bookings override availability status
  const allAvailableStaff = staffInfo.filter(
    s => s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME
  );
  const unavailableStaffRaw = staffInfo.filter(s => s.availability === StaffAvailability.UNAVAILABLE);

  // Unavailable staff with bookings should show in scheduled with "Unavailable" label
  const unavailableWithBookings = unavailableStaffRaw
    .filter(s => (s.bookedHours || 0) > 0)
    .map(s => ({ ...s, originalAvailability: StaffAvailability.UNAVAILABLE as const }));

  const trulyUnavailableStaff = unavailableStaffRaw.filter(s => (s.bookedHours || 0) === 0);

  // Combine all scheduled staff: available with bookings + unavailable with bookings + pending with bookings
  const scheduledFromAvailable = allAvailableStaff
    .filter(s => (s.bookedHours || 0) > 0)
    .map(s => ({ ...s, originalAvailability: undefined }));

  const scheduledStaff = [
    ...scheduledFromAvailable,
    ...unavailableWithBookings,
    ...pendingStaffWithBookings.scheduled,
  ];

  const trulyAvailableStaff = allAvailableStaff.filter(s => (s.bookedHours || 0) === 0);
  const remainingPendingStaff = pendingStaffWithBookings.remaining;

  const tabItems = [
    {
      key: "scheduled",
      label: `Scheduled (${scheduledStaff.length})`,
      children: (
        <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
          {loadingAllTickets ? (
            <div className="flex justify-center py-8">
              <Spin size="small" />
            </div>
          ) : scheduledStaff.length > 0 ? (
            scheduledStaff.map((staff, idx) => (
              <div
                key={idx}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => handleStaffClick(staff)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-blue-900">
                    {staff.staffName}
                    {/* Show original availability status label */}
                    {staff.originalAvailability === StaffAvailability.UNAVAILABLE && (
                      <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                        Unavailable
                      </span>
                    )}
                    {staff.originalAvailability === 'PENDING' && (
                      <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                        Pending
                      </span>
                    )}
                    {!staff.originalAvailability && staff.availability === StaffAvailability.PART_TIME && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Part-Time
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {staff.isDriver && (
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">
                        D
                      </span>
                    )}
                    {staff.isLead && (
                      <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">
                        L
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Available Hours:</span>
                  <span className="font-semibold text-blue-700">
                    {Number(staff.availableHours || 8).toFixed(1)} hrs
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-600">Booked:</span>
                  <span className="font-semibold text-gray-700">
                    {Number(staff.bookedHours || 0).toFixed(1)} hrs
                  </span>
                </div>
                {staff.note && (
                  <div className="text-xs text-gray-600 italic mt-2 pt-2 border-t border-blue-200">
                    {staff.note}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-gray-500 italic text-sm text-center py-8">
              No scheduled staff
            </div>
          )}
        </div>
      ),
    },
    {
      key: "available",
      label: `Available (${trulyAvailableStaff.length})`,
      children: (
        <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
          {trulyAvailableStaff.length > 0 ? (
            trulyAvailableStaff.map((staff, idx) => (
              <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-green-900">
                    {staff.staffName}
                    {staff.availability === StaffAvailability.PART_TIME && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Part-Time
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {staff.isDriver && (
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">
                        D
                      </span>
                    )}
                    {staff.isLead && (
                      <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">
                        L
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Available Hours:</span>
                  <span className="font-semibold text-green-700">
                    {Number(staff.availableHours || 8).toFixed(1)} hrs
                  </span>
                </div>
                {staff.note && (
                  <div className="text-xs text-gray-600 italic mt-2 pt-2 border-t border-green-200">
                    {staff.note}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-gray-500 italic text-sm text-center py-8">
              No fully available staff
            </div>
          )}
        </div>
      ),
    },
    {
      key: "unavailable",
      label: `Unavailable (${trulyUnavailableStaff.length})`,
      children: (
        <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
          {trulyUnavailableStaff.length > 0 ? (
            trulyUnavailableStaff.map((staff, idx) => (
              <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-red-900">{staff.staffName}</div>
                  <div className="flex gap-1">
                    {staff.isDriver && (
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">
                        D
                      </span>
                    )}
                    {staff.isLead && (
                      <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">
                        L
                      </span>
                    )}
                  </div>
                </div>
                {staff.note && (
                  <div className="text-xs text-gray-600 italic mt-2">
                    {staff.note}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-gray-500 italic text-sm text-center py-8">
              No unavailable staff
            </div>
          )}
        </div>
      ),
    },
    {
      key: "pending",
      label: `Pending (${remainingPendingStaff.length})`,
      children: (
        <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
          {remainingPendingStaff.length > 0 ? (
            remainingPendingStaff.map((staff, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div className="font-medium text-gray-900">{staff.staffName}</div>
                  <div className="flex gap-1">
                    {staff.isDriver && (
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">
                        D
                      </span>
                    )}
                    {staff.isLead && (
                      <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">
                        L
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500 italic mt-1">
                  No availability submitted
                </div>
              </div>
            ))
          ) : (
            <div className="text-gray-500 italic text-sm text-center py-8">
              No pending staff
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="border rounded-lg bg-white h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="font-semibold text-lg mb-1">Staff Details</div>
        <div className="text-sm text-gray-600">
          {selectedDate.format("dddd, MMMM D, YYYY")}
        </div>
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2 mt-3">
          <div className="text-center">
            <div className="text-xl font-bold text-blue-600">{scheduledStaff.length}</div>
            <div className="text-xs text-gray-600">Scheduled</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{trulyAvailableStaff.length}</div>
            <div className="text-xs text-gray-600">Available</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-red-600">{trulyUnavailableStaff.length}</div>
            <div className="text-xs text-gray-600">Unavailable</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-gray-500">{remainingPendingStaff.length}</div>
            <div className="text-xs text-gray-600">Pending</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden p-4">
        <Tabs items={tabItems} defaultActiveKey="scheduled" />
      </div>

      {/* Service Tickets Modal */}
      <Modal
        title={`${selectedStaff?.staffName}'s Schedule - ${selectedDate.format("MMM D, YYYY")}`}
        open={isModalOpen}
        onCancel={handleCloseModal}
        width={900}
        footer={null}
      >
        <Table
          dataSource={serviceTicketsData?.items || []}
          loading={loadingTickets}
          rowKey="id"
          pagination={false}
          scroll={{ y: 400 }}
          columns={[
            {
              title: "Serial",
              dataIndex: "serial",
              key: "serial",
              width: 120,
              render: (serial) => <span className="font-mono text-sm">{serial}</span>,
            },
            {
              title: "Booking Client",
              key: "bookingClient",
              width: 150,
              render: (_, record) => {
                const client = record.jobTicket?.client;
                if (!client) return "-";
                return client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || "-";
              },
            },
            {
              title: "Service",
              dataIndex: "serviceType",
              key: "serviceType",
              width: 120,
              render: (serviceType) => serviceType?.replace(/_/g, ' ') || '-',
            },
            {
              title: "Service Client",
              key: "serviceClient",
              width: 150,
              render: (_, record) => {
                const client = record.client;
                if (!client) return "-";
                return client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || "-";
              },
            },
            {
              title: "Location",
              key: "location",
              width: 150,
              render: (_, record) => {
                const loc = record.location;
                if (!loc) return "-";
                return loc.addressName || loc.city || "-";
              },
            },
            {
              title: "Duration",
              dataIndex: "duration",
              key: "duration",
              width: 100,
              render: (duration) => `${duration} hrs`,
            },
            {
              title: "Vessel",
              key: "vessel",
              width: 120,
              render: (_, record) => record.jobTicket?.booking?.vessel?.name || "-",
            },
          ]}
        />
      </Modal>
    </div>
  );
};
